import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import TablePagination from "./TablePagination";
import ReportsApi from "../Api/ReportsApi";
import ProductApi from "../Api/ProductApi";
import StandardDatePicker from "./StandardDatePicker";
import usePermissions from "../hook/usePermissions";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const StockReportsLayer = () => {
    const [reportData, setReportData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Default dates: this month's starting date and today
    const [fromDate, setFromDate] = useState(() => {
        const date = new Date();
        return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
    });
    const [toDate, setToDate] = useState(() => {
        return new Date().toISOString().split('T')[0];
    });

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [products, setProducts] = useState([]);

    const { hasPermission } = usePermissions();

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const res = await ProductApi.getAll(0, 100); // Fetch up to 100 products for filter
                if (res.status && res.response.data) {
                    setProducts(res.response.data.map(p => ({ value: p._id, label: p.name })));
                }
            } catch (error) {
                console.error("Error fetching products:", error);
            }
        };
        fetchProducts();
    }, []);

    useEffect(() => {
        fetchReport();
    }, [currentPage, rowsPerPage]);

    const fetchReport = async () => {
        setIsLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: rowsPerPage,
                productId: selectedProduct?.value || undefined,
                fromDate: fromDate || undefined,
                toDate: toDate || undefined
            };
            const res = await ReportsApi.getStockReports(params);
            if (res.status) {
                setReportData(res.response.data || []);
                setTotalRecords(res.response.total || 0);
                setTotalPages(Math.ceil((res.response.total || 0) / rowsPerPage));
            }
        } catch (error) {
            console.error("Error fetching stock report:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClearFilters = () => {
        const firstDayOfMonth = new Date();
        firstDayOfMonth.setDate(1);
        setFromDate(firstDayOfMonth.toISOString().split('T')[0]);
        setToDate(new Date().toISOString().split('T')[0]);
        setSelectedProduct(null);
        if (currentPage === 0) fetchReport();
        else setCurrentPage(0);
    };

    const handleExportExcel = async () => {
        toast.info("Preparing Excel file...");
        try {
            const params = {
                page: 0,
                limit: -1,
                productId: selectedProduct?.value || undefined,
                fromDate: fromDate || undefined,
                toDate: toDate || undefined
            };
            const res = await ReportsApi.getStockReports(params);
            if (res.status && res.response.data) {
                const data = res.response.data.map((item, index) => ({
                    "S.No": index + 1,
                    "Date": item.createdAt ? new Date(item.createdAt).toLocaleString() : "-",
                    "Product Name": item.productName || "-",
                    "Variant": item.variantLabel || item.attributeId || "-",
                    "Prev Stock": item.previousStock || 0,
                    "Change": item.quantity > 0 ? `+${item.quantity}` : item.quantity,
                    "Current Stock": item.currentStock || 0,
                    "Type": (item.type || "Manual").toUpperCase(),
                    "Reference": item.referenceLabel || item.referenceId || "N/A",
                    "Created By": item.createdByName || "System",
                    "Description": item.description || "-"
                }));

                const worksheet = XLSX.utils.json_to_sheet(data);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Report");
                XLSX.writeFile(workbook, `Stock_Report_${new Date().getTime()}.xlsx`);
                toast.success("Excel downloaded successfully");
            } else toast.error("Failed to export data");
        } catch (error) {
            console.error(error);
            toast.error("An error occurred during export");
        }
    };

    const getTypeBadge = (type) => {
        const t = (type || "").toLowerCase();
        switch (t) {
            case "initial": return <span className="badge bg-info-focus text-info-main px-24 py-4 radius-4">Initial</span>;
            case "order": return <span className="badge bg-danger-focus text-danger-main px-24 py-4 radius-4">Order</span>;
            case "stock_update": return <span className="badge bg-success-focus text-success-main px-24 py-4 radius-4">Physical</span>;
            case "purchase": return <span className="badge bg-primary-focus text-primary-main px-24 py-4 radius-4">Purchase</span>;
            case "return": return <span className="badge bg-success-focus text-success-main px-24 py-4 radius-4">Return</span>;
            case "cancel": return <span className="badge bg-warning-focus text-warning-main px-24 py-4 radius-4">Cancel</span>;
            default: return <span className="badge bg-secondary-focus text-secondary-main px-24 py-4 radius-4">{type || "Manual"}</span>;
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
                    <h6 className="text-primary-600 pb-2 mb-0">Stock Reports</h6>
                </div>

                <div className="row g-3 align-items-end">
                    <div className="col-xl col-md-3 col-sm-6">
                        <label className="form-label fw-bold">From Date</label>
                        <StandardDatePicker name="fromDate" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="form-control radius-8" />
                    </div>
                    <div className="col-xl col-md-3 col-sm-6">
                        <label className="form-label fw-bold">To Date</label>
                        <StandardDatePicker name="toDate" value={toDate} onChange={(e) => setToDate(e.target.value)} className="form-control radius-8" />
                    </div>
                    <div className="col-xl col-md-3 col-sm-6">
                        <label className="form-label fw-bold">Product (Optional)</label>
                        <Select options={products} value={selectedProduct} onChange={setSelectedProduct} placeholder="All Products" styles={selectStyles()} isClearable />
                    </div>
                    <div className="col-xl-auto col-md-3 col-sm-6 d-flex align-items-end gap-2">
                        <button type="button" onClick={() => { setCurrentPage(0); fetchReport(); }} className="btn btn-primary d-flex align-items-center gap-2 radius-8 h-40-px">Filter</button>
                        <button type="button" onClick={handleClearFilters} className="btn btn-outline-danger d-flex align-items-center gap-2 radius-8 h-40-px text-nowrap">
                            <Icon icon="solar:filter-remove-bold-duotone" fontSize={20} /> Clear
                        </button>
                    </div>
                </div>
            </div>

            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>S.No</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Date</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Product</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>SKU/Variant</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Prev Stock</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Change</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Curr Stock</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Type</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Reference</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Created By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (<tr><td colSpan="9" className="text-center py-4">Data loading...</td></tr>) : reportData.length > 0 ? (
                                reportData.map((data, index) => (
                                    <tr key={index}>
                                        <td>{currentPage * rowsPerPage + index + 1}</td>
                                        <td>{data.createdAt ? new Date(data.createdAt).toLocaleString() : "-"}</td>
                                        <td>{data.productName}</td>
                                        <td>{data.variantLabel || data.attributeId || "-"}</td>
                                        <td>{data.previousStock}</td>
                                        <td className={data.quantity > 0 ? "text-success-main fw-bold" : "text-danger-main fw-bold"}>
                                            {data.quantity > 0 ? `+${data.quantity}` : data.quantity}
                                        </td>
                                        <td>{data.currentStock}</td>
                                        <td>{getTypeBadge(data.type)}</td>
                                        <td>{data.referenceLabel || data.referenceId || "N/A"}</td>
                                        <td><span className="text-secondary-light fw-medium">{data.createdByName || "System"}</span></td>
                                    </tr>
                                ))
                            ) : (<tr><td colSpan="9" className="text-center py-4">No reports found.</td></tr>)}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-24">
                    <div>
                        {hasPermission("Stock Reports") && (
                            <button type="button" className="btn btn-success radius-8 px-20 py-11 d-flex align-items-center gap-2" onClick={handleExportExcel}>
                                <Icon icon="lucide:download" className="text-xl" /> Export Excel
                            </button>
                        )}
                    </div>
                    <div className="flex-grow-1">
                        <TablePagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setCurrentPage(0); }} totalRecords={totalRecords} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StockReportsLayer;
